import { api } from '@/lib/api-client'
import type {
  AdminTripRequest,
  BookingChangeLogEntry,
  CancelBookingRequest,
  CancellationOptions,
  MessageResponse,
  PageResponse,
  RescheduleBookingRequest,
  TripRequestFilters,
} from '@/types/api'

export function fetchTripRequests(filters: TripRequestFilters): Promise<PageResponse<AdminTripRequest>> {
  return api.get<PageResponse<AdminTripRequest>>('/api/admin/trip-requests', { query: { ...filters } })
}

export function fetchTripRequest(id: number): Promise<AdminTripRequest> {
  return api.get<AdminTripRequest>(`/api/admin/trip-requests/${id}`)
}

/**
 * Approves a pending request: confirms it and creates the trip. The API refuses
 * anything that is not still pending, so a double click cannot produce two trips.
 */
export function assignDriver(requestId: number, driverId: number): Promise<MessageResponse> {
  return api.patch<MessageResponse>(`/api/admin/trips/${requestId}/assign/${driverId}`)
}

/**
 * The server-computed facts the cancellation dialog is built from: which
 * question to ask, what the policy would do, and the ceiling on any refund.
 *
 * The dashboard never works this out from a payment-status string — the same
 * rule the refund eligibility endpoint already establishes.
 */
export function fetchCancellationOptions(requestId: number): Promise<CancellationOptions> {
  return api.get<CancellationOptions>(`/api/admin/trips/${requestId}/cancellation-options`)
}

/**
 * Cancels a booking and carries out the operator's decision about its money.
 *
 * **Breaking change.** This endpoint used to take no body and refund the whole
 * captured amount silently; it now requires a decision and answers 400 without
 * one. Refunding in full is one choice among several, and which one was made is
 * recorded.
 */
export function cancelTripRequest(
  requestId: number,
  decision: CancelBookingRequest,
): Promise<MessageResponse> {
  return api.patch<MessageResponse>(`/api/admin/trips/${requestId}/cancel`, decision)
}

/**
 * Moves a booking's times before it has become a trip — and after, since the
 * server moves the trip with it.
 *
 * There was no endpoint for this at all until now: a customer ringing to change
 * their pickup had to have the booking cancelled and re-made, which lost the
 * reference, the promo and, on a card booking, the payment.
 */
export function rescheduleBooking(
  requestId: number,
  change: RescheduleBookingRequest,
): Promise<MessageResponse> {
  return api.patch<MessageResponse>(`/api/admin/trip-requests/${requestId}/schedule`, change)
}

/** Every operator change to this booking, newest first. */
export function fetchBookingChangeLog(requestId: number): Promise<BookingChangeLogEntry[]> {
  return api.get<BookingChangeLogEntry[]>(`/api/admin/trip-requests/${requestId}/change-log`)
}
